import type { ReactNode } from 'react';
import type { InternalUserType } from '@ricoh/ria.client.core/model';
import type { Nullable } from '@ricoh/ria.client.core/types/core/data.type';

import { createElement } from 'react';
import { matchPath } from 'react-router';
import LoaderOverlay from '@ricoh/ria.client.core/components/indicator/loader/overlay';
import { Z_INDEX } from '@ricoh/ria.client.core/constants/ui/layout';
import { Url } from '@ricoh/ria.client.core/utils/es/url';

import type { AuthConfig } from './_config.type';

import authConfig from './_config';

/**
 * Create RedirectUrl.
 *
 * @param {keyof AuthConfig['redirect']} name RedirectUrl name.
 * @param {Nullable<string>} callbackPath Callback path.
 *
 * @returns {Url} RedirectUrl.
 */
function cerateRedirectUrl(name: keyof AuthConfig['redirect'], callbackPath?: Nullable<string>): Url {
  const url = new Url().resolve(authConfig.redirect[name]);
  if (callbackPath) {
    const callbackUrl = new Url(callbackPath);

    if (
      // * Same host
      callbackUrl.getHost() === url.getHost() &&
      // * Not guest or unverified path
      ![...authConfig.routes.guest, ...authConfig.routes.unverifiedUser].some((route) => matchPath(route, callbackUrl.pathname))
    ) {
      url.setSearchParams({
        redirectUrl: callbackUrl.toString(),
      });
    }
  }

  return url;
}

/** Authenticator plugin. */
const authenticator = {
  /**
   * Redirect to path with checking role.
   *
   * @param {InternalUserType} userType User role.
   * @param {Nullable<string>} redirectUrlPath RedirectUrlPath.
   *
   * @returns {ReactNode} Navigate ReactNode.
   */
  redirect(userType: InternalUserType, redirectUrlPath: Nullable<string>): ReactNode {
    const routes = authConfig.routes[userType];

    if (redirectUrlPath) {
      const redirectUrl = new Url(redirectUrlPath);
      if (routes.some((route) => matchPath(route, redirectUrl.pathname))) {
        redirectUrl.redirect();
        return null;
      }
    }

    const redirectRoute = routes.at(0);
    if (!redirectRoute) {
      return null;
    }

    new Url(redirectRoute).redirect();

    return createElement(LoaderOverlay, {
      opacity: 1,
      zIndex: Z_INDEX.MAX,
    });
  },

  /**
   * Redirect to SignIn.
   *
   * @param {Nullable<string>} redirectUrlPath RedirectUrlPath.
   *
   * @returns {ReactNode} Navigate ReactNode.
   */
  redirectSignin(redirectUrlPath?: Nullable<string>): ReactNode {
    const url = cerateRedirectUrl('signin', redirectUrlPath);
    url.redirect();

    return createElement(LoaderOverlay, {
      opacity: 1,
      zIndex: Z_INDEX.MAX,
    });
  },
};

export default authenticator;
